import { useState } from 'react';
import { Box, Button, Grid, OutlinedInput, Switch, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { isHipaaLink } from '../../../../config/productProfile';

import { IApiRequests, useApiRequests } from 'hooks';
import { ApiUser } from 'types';
import { useAppDispatch } from 'store';
import { getUser, selectUser } from 'store/user';
import { Info, Spinner } from 'components';
import { SmsPhonePopup } from './SmsPhonePopup';

import styles from './NotificationsPanel.module.scss';

type RoomField = 'receiveMessages' | 'notifications' | 'notificationsSound';

export const NotificationsPanel = () => {
  const { t, i18n } = useTranslation('settings');
  const isRtl = i18n.dir() === 'rtl';
  const user: ApiUser = useSelector(selectUser)!;
  const { updateCheckInNotificationPreferences, updateClinicUser }: IApiRequests = useApiRequests();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [openPhonePopup, setOpenPhonePopup] = useState(false);

  const handleToggle = async (field: 'enabled' | 'sms' | 'email' | 'sendFirstName'): Promise<void> => {
    const current = {
      checkInNotificationEnabled: user.clinicUser.checkInNotificationEnabled,
      checkInNotificationBySms: user.clinicUser.checkInNotificationBySms,
      checkInNotificationByEmail: user.clinicUser.checkInNotificationByEmail,
      checkInNotificationSendFirstName: user.clinicUser.checkInNotificationSendFirstName,
    };

    const next = { ...current };

    if (field === 'enabled') {
      next.checkInNotificationEnabled = !current.checkInNotificationEnabled;
    } else if (field === 'sms') {
      next.checkInNotificationBySms = !current.checkInNotificationBySms;
    } else if (field === 'email') {
      next.checkInNotificationByEmail = !current.checkInNotificationByEmail;
    } else {
      next.checkInNotificationSendFirstName = !current.checkInNotificationSendFirstName;
    }

    if (field === 'sms' && next.checkInNotificationBySms && !user.smsPhoneNumber) {
      setOpenPhonePopup(true);
      return;
    }

    setLoading(true);
    try {
      await updateCheckInNotificationPreferences(next);
      await dispatch(getUser());
      if (field === 'sms') {
        enqueueSnackbar(t('notificationsPanel.smsUpdated'), { variant: 'success' });
      } else if (field === 'email') {
        enqueueSnackbar(t('notificationsPanel.emailUpdated'), { variant: 'success' });
      } else if (field === 'sendFirstName') {
        enqueueSnackbar(t('notificationsPanel.notificationSettingUpdated'), { variant: 'success' });
      }
    } catch (error: any) {
      enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRoomToggle = async (field: RoomField): Promise<void> => {
    setLoading(true);
    try {
      await updateClinicUser({ [field]: !user.clinicUser[field] });
      await dispatch(getUser());
      enqueueSnackbar(t('notificationsPanel.notificationSettingUpdated'), { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Spinner />;

  const {
    checkInNotificationEnabled,
    checkInNotificationBySms,
    checkInNotificationByEmail,
    checkInNotificationSendFirstName,
  } = user.clinicUser;

  return (
    <>
      <Box className={styles.container}>
        <Grid className={classnames(styles.field, styles.headerField)}>
          <Typography variant='body4' className={styles.text}>
            {t('notificationsPanel.checkInNotifications')}
          </Typography>

          <Box className={styles.switcher}>
            <Switch checked={checkInNotificationEnabled} onChange={() => handleToggle('enabled')} />
          </Box>

          <Typography variant='body1' className={styles.description}>
            {t('notificationsPanel.description')}
          </Typography>
        </Grid>

        <Grid
          className={classnames(styles.field, styles.subField, {
            [styles.mainSettingsDisabled]: !checkInNotificationEnabled,
          })}
        >
          <span className={styles.titleWithInfo}>
            <Typography variant='body5'>
              {isHipaaLink
                ? t('notificationsPanel.includeClientFirstName')
                : t('notificationsPanel.includeGuestFirstName')}
            </Typography>
            <Info
              text={
                isHipaaLink
                  ? t('notificationsPanel.includeFirstNameInfo')
                  : t('notificationsPanel.includeFirstNameInfoVia3')
              }
            />
          </span>
          <Box className={styles.switcher}>
            <Switch
              checked={checkInNotificationEnabled && checkInNotificationSendFirstName}
              onChange={() => handleToggle('sendFirstName')}
            />
          </Box>
        </Grid>

        <Box
          className={classnames(styles.mainSettings, {
            [styles.mainSettingsDisabled]: !checkInNotificationEnabled,
          })}
        >
          <Grid className={styles.field}>
            <Typography variant='body4' className={styles.text}>
              {t('notificationsPanel.smsTextMessage')}
            </Typography>

            <Box className={styles.switcher}>
              <Switch
                checked={checkInNotificationEnabled && checkInNotificationBySms}
                onChange={() => handleToggle('sms')}
              />
            </Box>

            <Typography variant='body1' className={styles.description}>
              {isHipaaLink ? t('notificationsPanel.smsDescription') : t('notificationsPanel.smsDescriptionVia3')}
            </Typography>

            {checkInNotificationEnabled && checkInNotificationBySms && (
              <Box className={styles.phoneRow}>
                <OutlinedInput
                  sx={{ fontSize: '12px' }}
                  type='text'
                  value={user.smsPhoneNumber ?? ''}
                  disabled
                  inputProps={{ dir: 'ltr', style: { textAlign: isRtl ? 'right' : 'left' } }}
                />
                <Button className={styles.changeButton} variant='contained' onClick={() => setOpenPhonePopup(true)}>
                  {user.smsPhoneNumber ? t('securityPanel.change') : t('securityPanel.add')}
                </Button>
              </Box>
            )}
          </Grid>

          <Grid className={styles.field}>
            <Typography variant='body4' className={styles.text}>
              {t('notificationsPanel.email')}
            </Typography>

            <Box className={styles.switcher}>
              <Switch
                checked={checkInNotificationEnabled && checkInNotificationByEmail}
                onChange={() => handleToggle('email')}
              />
            </Box>

            <Typography variant='body1' className={styles.description}>
              {isHipaaLink ? t('notificationsPanel.emailDescription') : t('notificationsPanel.emailDescriptionVia3')}
            </Typography>

            {checkInNotificationEnabled && checkInNotificationByEmail && (
              <Box className={styles.emailRow}>
                <TextField size='small' fullWidth className={styles.disabledField} value={user.email} disabled />
              </Box>
            )}
          </Grid>
        </Box>
      </Box>

      <Box className={`${styles.container} ${styles.secondContainer}`}>
        <Grid className={styles.field}>
          <span className={styles.titleWithInfo}>
            <Typography variant='body5'>{t('roomSettingsForm.receiveWaitingRoomMessages')}</Typography>
            <Info
              text={t('receiveWaitingRoomMessagesInfo', { ns: 'providerRoom' })}
              tooltipPlacement={isRtl ? 'left' : 'right'}
            />
          </span>
          <Box className={styles.switcher}>
            <Switch checked={user.clinicUser.receiveMessages} onChange={() => handleRoomToggle('receiveMessages')} />
          </Box>
        </Grid>

        <Grid className={styles.field}>
          <Typography variant='body5'>{t('roomSettingsForm.notificationBanners')}</Typography>
          <Box className={styles.switcher}>
            <Switch checked={user.clinicUser.notifications} onChange={() => handleRoomToggle('notifications')} />
          </Box>
        </Grid>

        <Grid className={styles.field}>
          <Typography variant='body5'>{t('roomSettingsForm.notificationSound')}</Typography>
          <Box className={styles.switcher}>
            <Switch
              checked={user.clinicUser.notificationsSound}
              onChange={() => handleRoomToggle('notificationsSound')}
            />
          </Box>
        </Grid>
      </Box>

      <SmsPhonePopup
        open={openPhonePopup}
        handleClose={() => setOpenPhonePopup(false)}
        onVerified={async () => {
          try {
            await updateCheckInNotificationPreferences({
              checkInNotificationEnabled: user.clinicUser.checkInNotificationEnabled,
              checkInNotificationBySms: true,
              checkInNotificationByEmail: user.clinicUser.checkInNotificationByEmail,
              checkInNotificationSendFirstName: user.clinicUser.checkInNotificationSendFirstName,
            });
            await dispatch(getUser());
          } catch {
            // silently ignore — user can manually toggle if this fails
          }
        }}
      />
    </>
  );
};
